import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSubworkspaceStore } from "../../Context/store";
import { useWorkspaceStore } from "../../Context/store";
import {
  acceptJoinRequest,
  addWorkspace,
  createWorkspace,
  removeUserFromWorkspace,
  getAllPendingRequests,
  getWorkspaceByUserId,
  getWorkspaceUserByWorkspaceId,
  updateWorkspace,
  deleteWorkspace,
  getWorkspaceForumStatus,
} from "../../api/workspace/workspace";
import { useUserStore } from "../../Context/store";
import { getSubWorkspaceByWorkId } from "../../api/subworkspace/subworkspace";

// Hook to get workspaces by user ID

export const useGetAllPendingRequests = (userEmail) => {
  return useQuery({
    queryKey: ["pending-Requests", userEmail],
    queryFn: () => getAllPendingRequests(userEmail),
    select: (data) => data.data,
    enabled: !!userEmail,
  });
};
export const useGetWorkspaceWiseForumStatus = (user) => {
  return useQuery({
    queryKey: ["workspace-forum-status", user],
    queryFn: () => getWorkspaceForumStatus(user),
    select: (data) => data.data,
    enabled: !!user?.userId && !!user?.workspaceId,
  });
};

export const useAcceptJoinRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      return await acceptJoinRequest(body);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["workspaces"],
        }),
      ]);
    },
  });
};

export const useWorkspacesByUserId = (userId) => {
  return useQuery({
    queryKey: ["workspaces", userId],
    queryFn: () => getWorkspaceByUserId(userId),
    select: (data) => {
      return data.listWorkspaces;
    },
    enabled: !!userId,
  });
};

export const useAddWorkspace = () => {
  const queryClient = useQueryClient();
  const { selectWorkspace } = useWorkspaceStore();
  const { selectSubWorkspace } = useSubworkspaceStore();
  return useMutation({
    mutationFn: async (workspace) => {
      return await addWorkspace(workspace);
    },
    onSuccess: async (res) => {
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.userId;
      const data = await queryClient.fetchQuery({
        queryKey: ["workspaces", userId],
        queryFn: () => getWorkspaceByUserId(userId),
      });
      const findOne = data?.listWorkspaces.find(
        (workspace) => workspace.workSpaceId === res.workspaceId
      );
      if (findOne) {
        selectWorkspace(findOne);
        const subworkspaceList = await getSubWorkspaceByWorkId(
          res.workspaceId,
          userId
        );
        const subworkspace = subworkspaceList?.listOfSubworkspaces[0];
        if (subworkspace) {
          selectSubWorkspace(subworkspace);
        }
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });
};

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  const { selectWorkspace } = useWorkspaceStore();
  const { selectSubWorkspace } = useSubworkspaceStore();
  return useMutation({
    mutationFn: async (workspace) => {
      return await createWorkspace(workspace);
    },
    onSuccess: async (res) => {
      if (res?.error) {
        return res;
      }
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.userId;
      const data = await queryClient.fetchQuery({
        queryKey: ["workspaces", userId],
        queryFn: () => getWorkspaceByUserId(userId),
      });
      const findOne = data?.listWorkspaces.find(
        (workspace) => workspace.workSpaceId === res?.data?.workspaceId
      );
      if (findOne) {
        selectWorkspace(findOne);
        const subworkspaceList = await getSubWorkspaceByWorkId(
          res?.data?.workspaceId,
          userId
        );
        const subworkspace = subworkspaceList?.listOfSubworkspaces[0];
        if (subworkspace) {
          selectSubWorkspace(subworkspace);
        }
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });
};
// Hook to update an existing workspace
export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workspace) => {
      return await updateWorkspace(workspace);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
    onError: (error) => {
      console.log(error);
    },
  });
};

export const useGetWorkspaceUserByWorkId = (workId, search = "") => {
  const { user: loggedInUser } = useUserStore();

  return useQuery({
    queryKey: ["workspace-users", workId, loggedInUser?.userId, search],
    queryFn: async () =>
      await getWorkspaceUserByWorkspaceId(workId, loggedInUser?.userId, search),
    select: (data) => {
      const users = data.listWorkSpaceUsers.filter(
        (user) => user?.userId !== loggedInUser?.userId
      );
      return users;
    },
    enabled: (!!workId && !!loggedInUser?.userId) || !!search,
  });
};

export const useRemoveUserFromWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deleteUser) => {
      return await removeUserFromWorkspace(deleteUser);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["workspace-users"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["workspaces"],
        }),
      ]);
    },
    onError: (error) => {
      console.log(error);
    },
  });
};
export const useDeleteUserFromWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deleteUser) => {
      return await deleteWorkspace(deleteUser);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["workspace-users"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["workspaces"],
        }),
      ]);
    },
    onError: (error) => {
      console.log(error);
    },
  });
};
